import React, { useContext, useEffect, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import CancelIcon from '@mui/icons-material/Cancel'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import moment from 'moment'
import API from '../../../services/API'
import useIsMobile from '../../../helpers/useIsMobile'
import { GlobalContext } from '../../../App'
import AUDIT_CRITERIA from './auditCriteria'
import Age from '../../../components/Age'
import EmployeeSelector from '../../events/EmployeeSelector'

const fullName = (person) =>
  person ? `${person.firstName} ${person.lastName}` : 'Unknown'

const patientLabel = (epcr) => {
  const demographics = epcr?.demographics
  if (!demographics?.firstName && !demographics?.lastName) return 'Unknown patient'

  const age = demographics?.dob ? ` (${Age(demographics.dob, epcr?.createdAt)})` : ''
  return `${demographics?.firstName ?? ''} ${demographics?.lastName ?? ''}${age}`.trim()
}

// Complete (or review) a single ePCR record audit. `audit` carries the
// populated record summary plus `viewerInvolved` computed by the server.
// My Audits passes hideOpenRecord (clinicians can't open their submitted
// records) and onMarkReviewed for audits awaiting the owner's review.
// The Governance list passes onClinicianChanged so the audited clinician can
// be changed, e.g. when a clerk started the record for the treating clinician.
const AuditDialog = ({
  audit,
  preventSelfAudit,
  onClose,
  onCompleted,
  hideOpenRecord,
  onMarkReviewed,
  onClinicianChanged,
}) => {
  const { setToast } = useContext(GlobalContext)
  const isPhone = useIsMobile('sm')
  const [responses, setResponses] = useState([])
  const [feedback, setFeedback] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)
  const [editingClinician, setEditingClinician] = useState(false)
  const [clinicianPick, setClinicianPick] = useState(null)
  const [savingClinician, setSavingClinician] = useState(false)

  const open = !!audit
  const completed = audit?.status === 'Completed'
  const blocked = !completed && audit?.viewerInvolved && preventSelfAudit

  // Keyed on id/status rather than the object: the list reloads after the
  // clinician changes and must not wipe answers already entered.
  useEffect(() => {
    if (!audit) return
    setEditingClinician(false)
    setClinicianPick(null)
    setError(null)
    setFeedback(audit.feedback ?? '')
    setResponses(
      completed
        ? audit.responses
        : AUDIT_CRITERIA.map((criterion) => ({
            name: criterion.name,
            guidance: criterion.guidance,
            compliant: null,
            comment: '',
          })),
    )
  }, [audit?._id, audit?.status])

  const setResponse = (index, changes) =>
    setResponses((prev) =>
      prev.map((response, i) =>
        i === index ? { ...response, ...changes } : response,
      ),
    )

  const allAnswered = responses.every(
    (response) => typeof response.compliant === 'boolean',
  )

  const handleSubmit = () => {
    setSaving(true)
    setError(null)
    API.post(`/epcr-audits/${audit._id}/complete`, {
      responses: responses.map(({ name, compliant, comment }) => ({
        name,
        compliant,
        comment,
      })),
      overallCompliant: responses.every((response) => response.compliant),
      feedback,
    })
      .then((res) => {
        if (res.status === 200) {
          setToast({ message: 'Audit completed' })
          onCompleted()
        } else {
          setError(res.data?.error ?? 'Unable to complete this audit')
        }
      })
      .finally(() => setSaving(false))
  }

  const saveClinician = (clinician) => {
    setSavingClinician(true)
    API.put(`/epcr-audits/${audit._id}/clinician`, { clinician })
      .then((res) => {
        if (res.status === 200) {
          setToast({ message: 'Audited clinician updated' })
          setEditingClinician(false)
          setClinicianPick(null)
          onClinicianChanged()
        } else {
          setToast({
            message: res.data?.error ?? 'Unable to change the clinician',
            severity: 'error',
          })
        }
      })
      .finally(() => setSavingClinician(false))
  }

  if (!audit) return null

  const startedBy = audit.epcr?.review?.completedBy
  const clinician = audit.clinician

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      fullScreen={isPhone}
    >
      <DialogTitle sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          gap={1}
          flexWrap="wrap"
        >
          <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
            <>Record Audit{audit.epcr?.epcrNumber ? ` — ePCR ${audit.epcr.epcrNumber}` : ''}</>
            {completed && (
              <Chip
                size="small"
                sx={{ borderRadius: 1 }}
                color={audit.overallCompliant ? 'success' : 'error'}
                label={audit.overallCompliant ? 'Compliant' : 'Not Compliant'}
              />
            )}
          </Stack>
          {!hideOpenRecord && (
            <Button
              size="small"
              variant="outlined"
              endIcon={<OpenInNewIcon />}
              onClick={() =>
                window.open(
                  `/patient-report-forms/${audit.epcr?._id}`,
                  '_blank',
                  'noopener,noreferrer',
                )
              }
            >
              Open Record
            </Button>
          )}
        </Stack>
      </DialogTitle>
      <DialogContent>
        <Stack gap={2}>
          <Stack direction="row" gap={1} flexWrap="wrap" alignItems="center">
            <Typography variant="body2">
              {patientLabel(audit.epcr)} —{' '}
              {moment(
                audit.epcr?.incident?.incidentDate ?? audit.epcr?.createdAt,
              ).format('DD/MM/YYYY')}
            </Typography>
          </Stack>
          {editingClinician ? (
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              gap={1}
              alignItems={{ xs: 'stretch', sm: 'center' }}
            >
              <Box flex={1} minWidth={240}>
                <EmployeeSelector
                  value={clinicianPick}
                  onChange={(e, value) => setClinicianPick(value)}
                  label="Clinician being audited"
                  size="small"
                  fullWidth
                />
              </Box>
              <Button
                size="small"
                variant="contained"
                disabled={!clinicianPick || savingClinician}
                onClick={() => saveClinician(clinicianPick._id)}
              >
                Save
              </Button>
              {audit.auditedClinician && (
                <Button
                  size="small"
                  disabled={savingClinician}
                  onClick={() => saveClinician(null)}
                >
                  Reset to record owner
                </Button>
              )}
              <Button
                size="small"
                color="inherit"
                disabled={savingClinician}
                onClick={() => setEditingClinician(false)}
              >
                Cancel
              </Button>
            </Stack>
          ) : (
            <Stack direction="row" gap={1} flexWrap="wrap" alignItems="center">
              <Typography variant="body2">
                Clinician: <strong>{fullName(clinician)}</strong>
                {audit.auditedClinician && ' (chosen by Governance)'}
                {startedBy &&
                  clinician &&
                  String(startedBy._id) !== String(clinician._id) &&
                  ` — record started by ${fullName(startedBy)}`}
              </Typography>
              {onClinicianChanged && (
                <Button
                  size="small"
                  sx={{ minWidth: 0, py: 0 }}
                  onClick={() => setEditingClinician(true)}
                >
                  Change
                </Button>
              )}
            </Stack>
          )}
          <Stack direction="row" gap={1} flexWrap="wrap">
            {(audit.reasons || []).map((reason) => (
              <Chip
                key={reason}
                label={reason}
                size="small"
                color="anPurple"
                sx={{ borderRadius: 1 }}
              />
            ))}
          </Stack>

          {audit.raisedBy && (
            <Typography variant="body2" color="text.secondary">
              Raised by {fullName(audit.raisedBy)}
              {audit.raisedNote ? ` — ${audit.raisedNote}` : ''}
            </Typography>
          )}

          {blocked && (
            <Alert severity="error">
              You were involved in this record, so this audit must be completed
              by another member of the Governance team.
            </Alert>
          )}
          {!completed && audit.viewerInvolved && !preventSelfAudit && (
            <Alert severity="warning">
              You were involved in this record. Where possible, record audits
              should be completed by someone who was not involved in the
              patient's care.
            </Alert>
          )}
          {completed && (
            <Alert severity="info">
              Audited by{' '}
              {audit.completedBy
                ? `${audit.completedBy.firstName} ${audit.completedBy.lastName}`
                : 'Unknown'}{' '}
              on {moment(audit.completedAt).format('DD/MM/YYYY HH:mm')}
              {audit.reviewedByOwner &&
                ` — reviewed by the clinician${
                  audit.reviewedByOwnerAt
                    ? ` on ${moment(audit.reviewedByOwnerAt).format('DD/MM/YYYY')}`
                    : ''
                }`}
            </Alert>
          )}

          <Divider />

          {responses.map((response, index) => (
            <Stack key={response.name} gap={1}>
              <Stack
                direction={{ xs: completed ? 'row' : 'column', sm: 'row' }}
                alignItems={{ xs: completed ? 'center' : 'stretch', sm: 'center' }}
                justifyContent="space-between"
                gap={1}
                flexWrap={{ xs: 'nowrap', sm: 'wrap' }}
              >
                <Stack
                  direction="row"
                  alignItems="center"
                  gap={0.5}
                  sx={{ flex: { xs: 1, sm: 'initial' }, minWidth: 0 }}
                >
                  <Typography variant="subtitle2">{response.name}</Typography>
                  {!completed && response.guidance && (
                    <Tooltip title={response.guidance}>
                      <InfoOutlinedIcon fontSize="small" color="info" />
                    </Tooltip>
                  )}
                </Stack>
                {completed ? (
                  response.compliant ? (
                    <CheckCircleIcon color="success" />
                  ) : (
                    <CancelIcon color="error" />
                  )
                ) : (
                  <ToggleButtonGroup
                    exclusive
                    size="small"
                    fullWidth={isPhone}
                    disabled={blocked}
                    value={response.compliant}
                    onChange={(e, value) => {
                      if (value !== null) setResponse(index, { compliant: value })
                    }}
                  >
                    <ToggleButton value={true} color="success">
                      Compliant
                    </ToggleButton>
                    <ToggleButton value={false} color="error">
                      Not Compliant
                    </ToggleButton>
                  </ToggleButtonGroup>
                )}
              </Stack>
              {completed ? (
                response.comment && (
                  <Typography variant="body2" color="text.secondary">
                    {response.comment}
                  </Typography>
                )
              ) : (
                <TextField
                  size="small"
                  fullWidth
                  multiline
                  disabled={blocked}
                  placeholder="Comments (optional)"
                  value={response.comment ?? ''}
                  onChange={(e) => setResponse(index, { comment: e.target.value })}
                />
              )}
            </Stack>
          ))}

          <Divider />

          {completed ? (
            audit.feedback && (
              <Box>
                <Typography variant="subtitle2">
                  Feedback for the clinician
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {audit.feedback}
                </Typography>
              </Box>
            )
          ) : (
            <TextField
              label="Feedback for the clinician (optional)"
              fullWidth
              multiline
              minRows={2}
              disabled={blocked}
              value={feedback}
              onChange={(e) => setFeedback(e.target.value)}
            />
          )}

          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        {completed && onMarkReviewed && (
          <Button variant="contained" onClick={() => onMarkReviewed(audit)}>
            Mark as Reviewed
          </Button>
        )}
        {!completed && (
          <Button
            variant="contained"
            color="success"
            disabled={blocked || !allAnswered || saving}
            onClick={handleSubmit}
          >
            Complete Audit
          </Button>
        )}
      </DialogActions>
    </Dialog>
  )
}

export default AuditDialog
